import { FC, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';

import circleStyles from '../landing-page/pages/home.module.css';
import clsx from 'clsx';
import { t } from 'i18next';

import PrimaryButton from 'shared/components/ui/PrimaryButton/PrimaryButton';
import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { MovesEnum, MovesEnumRaffle, RoundOutcome } from 'shared/constants/shared-enums';

import chestShadow from 'assets/png/daily-quests/chest-shadow.png?format=webp;avif&as=picture';
import rainbowChestImage from 'assets/png/daily-quests/rainbow-chest.png?format=webp;avif&as=picture';
import prizeBoxHeaderImage from 'assets/png/prize-box-header.png?format=webp;avif&as=picture';

import { HandSelector } from './components/hand-selector/hand-selector';
import { MINIMUM_POINTS_TO_PLAY, PERCENT_BY_TOPHAND } from './prize-box-page.constants';
import styles from './prize-box-page.module.css';
import {
  GamePhase,
  HandTypes,
  RoundResult,
  type HandType,
  type WinStatus,
} from './prize-box-page.types';
import { formatUsd } from './services/units';

export const HANDS: HandType[] = [HandTypes.Rock, HandTypes.Paper, HandTypes.Scissors];

export const handToMoveEnum: Record<HandType, MovesEnum> = {
  [HandTypes.Rock]: MovesEnum.Rock,
  [HandTypes.Paper]: MovesEnum.Paper,
  [HandTypes.Scissors]: MovesEnum.Scissors,
};

export const mapStatusToOutcome = (status: RoundResult): RoundOutcome => {
  switch (status) {
    case RoundResult.Win:
      return RoundOutcome.Win;
    case RoundResult.Lose:
      return RoundOutcome.Lose;
    default:
      return RoundOutcome.Draw;
  }
};

export const mapHandToMove = (hand: HandType | null): MovesEnum => {
  if (!hand) return MovesEnum.Rock;
  return handToMoveEnum[hand];
};

export const mapMoveToHand = (m: MovesEnum): HandType => {
  switch (m) {
    case MovesEnum.Rock:
      return HandTypes.Rock;
    case MovesEnum.Paper:
      return HandTypes.Paper;
    case MovesEnum.Scissors:
      return HandTypes.Scissors;
    default:
      return HandTypes.Rock;
  }
};

export const topHandIndex = (topHand: number): number => {
  if (topHand >= 1 && topHand <= PERCENT_BY_TOPHAND.length) return topHand;
  return Math.min(PERCENT_BY_TOPHAND.length - 1, Math.max(0, topHand));
};

export const handSharePctFromTopHand = (topHand: number): number => {
  const idx = topHandIndex(topHand);
  return PERCENT_BY_TOPHAND[idx];
};
export const actualSharePct = (winAmountCents: number, postSlotBalanceCents: number): number => {
  const tsSpendBefore = winAmountCents + postSlotBalanceCents;
  if (tsSpendBefore === 0) return 0;
  return Math.round((winAmountCents / tsSpendBefore) * 100);
};

export function handToContractMove(h: HandType): MovesEnumRaffle {
  if (h === HandTypes.Rock) return MovesEnumRaffle.Rock;
  if (h === HandTypes.Paper) return MovesEnumRaffle.Paper;
  if (h === HandTypes.Scissors) return MovesEnumRaffle.Scissors;
  throw new Error('InvalidMove');
}
export function statusFromWinner(w: number): WinStatus {
  if (w === 0) return RoundResult.Draw;
  if (w === 1) return RoundResult.Win;
  if (w === 2) return RoundResult.Lose;
  return RoundResult.Draw;
}
export function synthesizeAiHand(player: HandType, status: WinStatus): HandType {
  if (status === RoundResult.Draw) return player;
  if (status === RoundResult.Win) {
    if (player === HandTypes.Rock) return HandTypes.Scissors;
    if (player === HandTypes.Paper) return HandTypes.Rock;
    return HandTypes.Paper;
  }
  if (player === HandTypes.Rock) return HandTypes.Paper;
  if (player === HandTypes.Paper) return HandTypes.Scissors;
  return HandTypes.Rock;
}

export const HeaderPool: FC<{ label: string; poolCents: number }> = ({ label, poolCents }) => (
  <div className="relative mb-1 flex items-center justify-center sm:mb-5">
    <div className={styles.header}>
      {label}: ${formatUsd(poolCents, 0)}
    </div>
    <OptimizedImage
      imageData={prizeBoxHeaderImage}
      alt="pool-prize"
      className="mx-6 h-full w-full max-w-[37.5rem] object-cover sm:mx-0"
    />
  </div>
);

export const DescriptionBlock: FC<{ points: number }> = ({ points }) => (
  <div className="mb-0 flex flex-col items-center justify-center gap-1">
    <p className="text-center text-md font-semibold leading-5 text-white-text sm:text-lg sm:leading-6">
      <Trans
        i18nKey="prizeBox.description"
        values={{ amount: MINIMUM_POINTS_TO_PLAY, points }}
        components={[<span className="text-yellow" />, <span className="text-yellow" />]}
      />
    </p>
  </div>
);

export const ChestArea: FC<{ phase: GamePhase; tConfirming: string; attemptsLeft: number }> = ({
  phase,
  tConfirming,
  attemptsLeft,
}) => (
  <div className="relative flex flex-1 items-center justify-center">
    <div className="relative">
      {attemptsLeft > 0 && (
        <div
          className={clsx(
            circleStyles.circle,
            'absolute right-[8%] top-[20%] z-50 h-8 min-h-8 w-8 min-w-8 rounded-full border-2 text-xl [-webkit-text-stroke-width:2px] sm:h-8 sm:w-8',
          )}
        >
          {attemptsLeft}
        </div>
      )}
      <OptimizedImage
        imageData={chestShadow}
        alt="shadow"
        className={clsx(
          'pointer-events-none absolute bottom-0 top-4 w-full max-w-[18rem] scale-100 select-none transition-all duration-500 xs:top-8',
          'animate-scalePulse motion-reduce:animate-none',
          phase === GamePhase.Revealing ? 'scale-110 opacity-0' : 'opacity-100',
        )}
      />
      <OptimizedImage
        imageData={rainbowChestImage}
        alt="chest"
        className={clsx(
          'mx-auto h-full w-full max-w-60 object-cover motion-reduce:animate-none',
          phase === GamePhase.Revealing ? 'animate-pulse opacity-50' : 'animate-shake opacity-100',
        )}
      />
    </div>
    {phase === GamePhase.Revealing && (
      <p className="absolute inset-0 flex items-center justify-center text-3xl font-black text-white/75">
        {tConfirming}
      </p>
    )}
  </div>
);

export const FooterControls: FC<{
  phase: GamePhase;
  attemptsLeft: number;
  onSelect: (m: MovesEnum) => void;
  onBuy: () => void;
  onTryAgain: () => void;
  selectedMove: MovesEnum | null;
  selecting: boolean;
  className?: string;
  canPlayNow: boolean;
  loadingApprove?: boolean;
  poolBalance?: number;
}> = ({
  phase,
  attemptsLeft,
  onSelect,
  onBuy,
  onTryAgain,
  selectedMove,
  selecting,
  className,
  canPlayNow,
  loadingApprove,
  poolBalance,
}) => {
  const title = useMemo(() => {
    if (poolBalance <= 0) return t('prizeBox.waitForRefill');
    if (!canPlayNow) return t('prizeBox.needMorePointsToOpen');
    return t('prizeBox.chooseYourHand');
  }, [poolBalance, canPlayNow]);

  return (
    <div
      className={clsx('flex h-full w-full flex-col items-center justify-between gap-1', className)}
    >
      {phase !== GamePhase.Result && attemptsLeft > 0 && (
        <>
          <Link
            to="/daily-quests"
            className="mb-2 text-md font-semibold leading-normal text-[#FFDA42] underline lg:text-lg"
          >
            <p>{t('prizeBox.completeQuestsToGetBonusPoints')}</p>
          </Link>
          <div className={styles.info}>
            <p>{title}</p>
          </div>
          <HandSelector
            disabled={selecting || !canPlayNow || (poolBalance ?? 0) <= 0}
            onSelect={onSelect}
            selected={selectedMove}
            className="mt-3 sm:my-8"
          />
        </>
      )}
      {phase !== GamePhase.Result && attemptsLeft === 0 && (
        <PrimaryButton
          onClick={onBuy}
          disabled={loadingApprove}
          text={t('prizeBox.buyMoreChests')}
          className="mx-auto w-full sm:max-w-[70%]"
        />
      )}
      {phase === GamePhase.Result && (
        <PrimaryButton
          onClick={onTryAgain}
          className="w-full sm:max-w-[70%]"
          text={t('prizeBox.tryAgain')}
        />
      )}
    </div>
  );
};
